/**
 * NetWorthPage — single big-picture view of household wealth.
 *
 * Six-card breakdown · SVG waterfall · by-owner stacked bar ·
 * by-provider bar · key metrics row.
 *
 * Note: inline `style` props are intentional throughout this file —
 * dynamic palette colours (index-picked array) and computed percentage
 * widths cannot be expressed as static Tailwind classes. The same pattern
 * is used across all visualisation pages in this codebase.
 */
import { useEffect, useState } from 'react'
import { Wallet, RefreshCw, TrendingUp, TrendingDown, Home, Banknote, Coins } from 'lucide-react'
import { clsx } from 'clsx'
import { AreaChart, Area, ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend } from 'recharts'
import { api, downloadCsv, getFxSensitivity, postFxShock } from '@/lib/api'
import type { FxSensitivity, FxShockResponse } from '@/lib/types'
import PageHeader from '@/components/ui/PageHeader'
import PageLoader from '@/components/ui/PageLoader'
import ErrorAlert from '@/components/ui/ErrorAlert'

import { mergeIndexedSeries } from './netWorthHistory'
import type { BenchmarkMap, IndexedChartPoint } from './netWorthHistory'
import { fmtAgo, fmtDateTime, fmtGBP, fmtGBPCompact, fmtGBPSigned, fmtNum, fmtPct, fmtPctSigned, fmtSigned } from '@/lib/format'

interface HistoryRow { date: string; net_worth_gbp: number; change_gbp: number; change_pct: number }

type StaleSeverity = 'fresh' | 'stale' | 'very_stale' | 'unknown'

interface NetWorthHeroProps {
  netWorth: number
  /** ISO timestamp of the most recent holding reprice — the honest "as at". */
  repriceAt?: string | null
  staleSeverity?: StaleSeverity
}

function NetWorthHero({ netWorth, repriceAt, staleSeverity }: NetWorthHeroProps) {
  const [series, setSeries] = useState<Array<{ date: string; nav: number }>>([])
  const [showHistory, setShowHistory] = useState(false)
  const [history, setHistory] = useState<HistoryRow[]>([])
  const [benchmarks, setBenchmarks] = useState<BenchmarkMap>({})
  const [indexedChart, setIndexedChart] = useState<IndexedChartPoint[]>([])
  const [historyLoaded, setHistoryLoaded] = useState(false)
  const [csvError, setCsvError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.get('/api/reports/performance-series?days=30')
        const rows = Array.isArray(res.data) ? res.data : (res.data?.series ?? [])
        if (cancelled) return
        const clean = rows
          .filter((r: any) => r && r.portfolio_value != null)
          .map((r: any) => ({ date: r.date, nav: Number(r.portfolio_value ?? 0) }))
        setSeries(clean)
      } catch {
        if (!cancelled) setSeries([])
      }
    })()
    return () => { cancelled = true }
  }, [])

  // Lazy-load the real daily history the first time the panel is opened.
  useEffect(() => {
    if (!showHistory || historyLoaded) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.get('/api/net-worth/history?days=30')
        if (cancelled) return
        setHistory(Array.isArray(res.data?.history) ? res.data.history : [])
        const bm: BenchmarkMap = res.data?.benchmarks ?? {}
        setBenchmarks(bm)
        setIndexedChart(mergeIndexedSeries(bm))
      } catch {
        if (!cancelled) { setHistory([]); setBenchmarks({}); setIndexedChart([]) }
      } finally {
        if (!cancelled) setHistoryLoaded(true)
      }
    })()
    return () => { cancelled = true }
  }, [showHistory, historyLoaded])

  const first = series[0]?.nav ?? 0
  const last = series[series.length - 1]?.nav ?? 0
  const delta = last - first
  const pct = first > 0 ? (delta / first) * 100 : 0
  const up = delta >= 0

  return (
    <div className="card p-6 border border-purple/30 bg-purple/5">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="panel-title">
            Total Net Worth
          </p>
          {/* The number the whole platform exists to defend — display
              grade: gradient ink, tight tracking, tabular figures. */}
          <p className="stat-hero text-gradient mt-2 tabular">
            {fmtGBP(netWorth)}
          </p>
          {/* Honest "as at" — the API's last mark-to-market timestamp, NOT
              new Date(): a stale valuation must not be stamped as fresh.
              Same staleness-dot idiom as the Command Centre net-worth card. */}
          {repriceAt ? (() => {
            const severity = staleSeverity ?? 'unknown'
            const dotCls = severity === 'fresh'
              ? 'bg-emerald-400 animate-pulse'
              : severity === 'stale'
              ? 'bg-amber-400'
              : severity === 'very_stale'
              ? 'bg-red-500'
              : 'bg-text-muted'
            const labelCls = severity === 'very_stale'
              ? 'text-red-400 font-semibold'
              : severity === 'stale'
              ? 'text-amber-400'
              : 'text-text-muted'
            const prefix = severity === 'fresh'
              ? 'Live · priced'
              : severity === 'stale'
              ? 'Stale · last priced'
              : severity === 'very_stale'
              ? 'PRICES STALE · last priced'
              : 'As at'
            return (
              <p
                className="text-xs text-text-muted mt-2"
                title={`Last mark-to-market: ${repriceAt}\nSeverity: ${severity}`}
              >
                <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle ${dotCls}`} aria-hidden />
                <span className={labelCls}>
                  {prefix} {fmtAgo(repriceAt)} · {fmtDateTime(repriceAt)}
                </span>
              </p>
            )
          })() : (
            <p className="text-xs text-text-muted mt-2">
              Valuation time unknown — prices may be stale
            </p>
          )}
        </div>
        {series.length >= 2 && (
          <div className="flex-1 min-w-[220px]">
            <div className="flex items-center gap-2 justify-end mb-1 text-sm">
              <span className={up ? 'delta-chip-pos' : 'delta-chip-neg'}>
                {up ? <TrendingUp size={12} aria-hidden /> : <TrendingDown size={12} aria-hidden />}
                {fmtGBPSigned(delta)} ({up ? '+' : ''}{pct.toFixed(2)}%)
              </span>
              <span className="text-[10px] text-text-muted">· 30d</span>
            </div>
            <div className="h-20">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series}>
                  <defs>
                    <linearGradient id="nwHeroGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={up ? '#10b981' : '#f43f5e'} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={up ? '#10b981' : '#f43f5e'} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area type="monotone" dataKey="nav"
                    stroke={up ? '#10b981' : '#f43f5e'} strokeWidth={2}
                    fill="url(#nwHeroGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Always render the toggle — /api/net-worth/history self-heals, so
          daily history can exist even when the 30d sparkline series is empty. */}
      <div className="mt-4 pt-3 border-t border-purple/15">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setShowHistory((v) => !v)}
              className="text-xs text-purple-light hover:text-purple font-semibold"
            >
              {showHistory ? '▾ Hide daily history' : '▸ View daily history (real, persisted)'}
            </button>
            {showHistory && historyLoaded && history.length > 0 && (
              <button
                type="button"
                onClick={async () => {
                  // downloadCsv returns false on failure — surface it
                  const ok = await downloadCsv('/api/net-worth/history.csv?days=30', 'net-worth-history-30d.csv')
                  setCsvError(ok ? null : 'CSV export failed — please try again.')
                }}
                className="text-xs text-text-muted hover:text-text-secondary"
              >
                ⤓ Export CSV
              </button>
            )}
            {csvError && (
              <span className="text-xs text-rose-light" role="alert">{csvError}</span>
            )}
          </div>
          {showHistory && historyLoaded && (benchmarks.spy || benchmarks.qqq) && (
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs">
              <span className="text-text-muted">vs market (30d):</span>
              {(['spy', 'qqq'] as const).map((k) => {
                const b = benchmarks[k]
                if (!b) return null
                const beat = b.vs_net_worth_pct >= 0
                return (
                  <span key={k} className="font-mono">
                    <span className="text-text-secondary uppercase">{k}</span>{' '}
                    <span className="text-text-muted">{fmtPctSigned(b.window_pct, 1)}</span>{' · '}
                    <span className={clsx('font-semibold', beat ? 'text-emerald-400' : 'text-rose-400')}>
                      {beat ? 'ahead ' : 'behind '}{fmtSigned(b.vs_net_worth_pct, 1)}pt
                    </span>
                  </span>
                )
              })}
            </div>
          )}
          {showHistory && historyLoaded && indexedChart.length >= 2 && (
            <div className="mt-3 rounded-lg border border-white/5 p-3 bg-surface-hover">
              <p className="text-[10px] uppercase tracking-wider text-text-muted mb-2">
                Rebased to 100 at window start · you vs the market
              </p>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={indexedChart} margin={{ top: 4, right: 8, bottom: 0, left: -16 }}>
                    <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#9ca3af' }}
                      tickFormatter={(d: string) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                      minTickGap={24} />
                    <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} domain={['auto', 'auto']} width={36} />
                    <Tooltip
                      contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, fontSize: 11 }}
                      formatter={(v: any, name: string) => [v == null ? '—' : Number(v).toFixed(1), name]}
                      labelFormatter={(d: string) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Line type="monotone" dataKey="nw" name="Net worth" stroke="#a78bfa" strokeWidth={2} dot={false} connectNulls />
                    <Line type="monotone" dataKey="spy" name="SPY" stroke="#34d399" strokeWidth={1.5} dot={false} connectNulls />
                    <Line type="monotone" dataKey="qqq" name="QQQ" stroke="#f59e0b" strokeWidth={1.5} dot={false} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          {showHistory && (
            <div className="mt-3 max-h-72 overflow-y-auto overflow-x-auto rounded-lg border border-white/5">
              {!historyLoaded ? (
                <p className="text-xs text-text-muted p-3">Loading real daily snapshots…</p>
              ) : history.length === 0 ? (
                <p className="text-xs text-text-muted p-3">
                  No history yet — the midnight snapshot reconstruction is still warming up.
                </p>
              ) : (
                <table className="w-full text-xs tabular-nums">
                  <thead className="sticky top-0 bg-surface-card text-text-muted">
                    <tr className="text-left">
                      <th className="px-3 py-2 font-medium">Date</th>
                      <th className="px-3 py-2 font-medium text-right">Net worth</th>
                      <th className="px-3 py-2 font-medium text-right">Δ day</th>
                      <th className="px-3 py-2 font-medium text-right">Δ %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...history].reverse().map((r) => {
                      const rUp = r.change_gbp >= 0
                      return (
                        <tr key={r.date} className="border-t border-white/5">
                          <td className="px-3 py-1.5 text-text-secondary">
                            {new Date(r.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono text-text-primary">{fmtGBP(r.net_worth_gbp)}</td>
                          <td className={clsx('px-3 py-1.5 text-right font-mono', rUp ? 'text-emerald-400' : 'text-rose-400')}>
                            {r.change_gbp === 0 ? '—' : fmtGBPSigned(r.change_gbp)}
                          </td>
                          <td className={clsx('px-3 py-1.5 text-right', rUp ? 'text-emerald-400' : 'text-rose-400')}>
                            {r.change_gbp === 0 ? '—' : fmtPctSigned(r.change_pct, 2)}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
      </div>
    </div>
  )
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface BucketItem {
  name: string
  value_gbp: number
  pct_of_net_worth: number
}

interface CurrencyItem {
  currency: string
  value_gbp: number
  pct_of_tracked: number
}

interface AccountItem {
  id: number
  name: string
  type: string
  owner: string
  discretionary: boolean
  value_gbp: number
  holdings_count: number
}

interface NetWorthSummary {
  total_net_worth_gbp: number
  tracked_investments_gbp: number
  property_gross_gbp: number
  liabilities_gbp: number
  property_net_equity_gbp: number
  discretionary_gbp: number
  non_discretionary_gbp: number
  discretionary_pct: number
  by_bucket: BucketItem[]
  by_owner: BucketItem[]
  by_wrapper: BucketItem[]
  by_currency: CurrencyItem[]
  by_account: AccountItem[]
  /** ISO timestamp of the most recent holding reprice (mark-to-market freshness) */
  most_recent_reprice_at?: string | null
  /** Minutes elapsed since most_recent_reprice_at (server-computed) */
  prices_stale_minutes?: number | null
  /** Bucketed staleness: "fresh" (<30min) / "stale" (30min–6h) / "very_stale" (>6h) / "unknown" */
  prices_stale_severity?: StaleSeverity
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const pct = (n: number, total: number) =>
  total ? ((n / total) * 100).toFixed(1) + '%' : '—'

/** Pull a single bucket value by name (case-insensitive prefix match). */
function bucketVal(buckets: BucketItem[], ...names: string[]): number {
  const lower = names.map(n => n.toLowerCase())
  return buckets
    .filter(b => lower.some(n => b.name.toLowerCase().includes(n)))
    .reduce((sum, b) => sum + b.value_gbp, 0)
}

/** Extract a provider short-name from an account name, e.g. "AJ Bell ISA" → "AJ Bell" */
function providerFrom(name: string): string {
  const known = [
    'AJ Bell', 'IG', 'Hargreaves Lansdown', 'HL', 'Coinbase',
    'Robinhood', 'Premium Bonds', 'Nationwide', 'Barclays',
    'Halifax', 'Lloyds', 'HSBC', 'Vanguard', 'Trading 212',
    'Interactive Brokers', 'IBKR', 'Freetrade', 'Monzo', 'Starling',
  ]
  for (const k of known) {
    if (name.toLowerCase().includes(k.toLowerCase())) return k
  }
  return name.split(' ')[0] // fallback: first word
}

const COLOURS = [
  '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6',
  '#06b6d4', '#ec4899', '#84cc16', '#f97316',
]

// ─── SVG Waterfall ────────────────────────────────────────────────────────────

interface WaterfallBar {
  label: string
  value: number    // signed: +ve = add, –ve = subtract
  isTotal?: boolean
  colour?: string
}

function WaterfallChart({ bars }: { bars: WaterfallBar[] }) {
  const W = 680
  const H = 220
  const LEFT = 120
  const RIGHT = 20
  const TOP = 20
  const BOT = 36
  const chartW = W - LEFT - RIGHT
  const chartH = H - TOP - BOT

  // Build running totals to find y positions
  let running = 0
  const segments: Array<{
    label: string
    start: number    // running before this bar
    end: number      // running after this bar
    isTotal: boolean
    colour: string
  }> = bars.map((b) => {
    const start = b.isTotal ? 0 : running
    const end = b.isTotal ? b.value : running + b.value
    if (!b.isTotal) running = end
    return {
      label: b.label,
      start,
      end,
      isTotal: b.isTotal ?? false,
      colour: b.colour ?? (b.value >= 0 ? '#10b981' : '#ef4444'),
    }
  })

  const allVals = segments.flatMap(s => [s.start, s.end])
  const dataMax = Math.max(...allVals)
  const dataMin = Math.min(...allVals, 0)
  const span = dataMax - dataMin || 1

  const toY = (v: number) => TOP + chartH - ((v - dataMin) / span) * chartH
  const barW = Math.floor(chartW / bars.length) - 12

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" aria-label="Net worth waterfall chart">
      {/* Zero line */}
      <line
        x1={LEFT} x2={W - RIGHT}
        y1={toY(0)} y2={toY(0)}
        stroke="rgb(var(--border))" strokeWidth={1}
      />

      {segments.map((seg, i) => {
        const x = LEFT + i * (chartW / bars.length) + 6
        const y1 = toY(Math.max(seg.start, seg.end))
        const y2 = toY(Math.min(seg.start, seg.end))
        const barH = Math.max(Math.abs(y2 - y1), 3)
        const midY = (y1 + y2) / 2

        return (
          <g key={seg.label}>
            {/* Connector line from previous */}
            {i > 0 && !seg.isTotal && (
              <line
                x1={LEFT + (i - 1) * (chartW / bars.length) + 6 + barW}
                x2={x}
                y1={toY(segments[i - 1].end)}
                y2={toY(segments[i - 1].end)}
                stroke="rgb(var(--border))" strokeWidth={1} strokeDasharray="3 3"
              />
            )}
            {/* Bar */}
            <rect
              x={x} y={y1}
              width={barW} height={barH}
              fill={seg.colour}
              rx={3}
              opacity={seg.isTotal ? 1 : 0.85}
            />
            {/* Value label */}
            <text
              x={x + barW / 2}
              y={midY}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={10}
              fontFamily="ui-monospace, monospace"
              fill="white"
              fontWeight={seg.isTotal ? 700 : 400}
            >
              {fmtGBPCompact(seg.end - seg.start || seg.end)}
            </text>
            {/* X-axis label */}
            <text
              x={x + barW / 2}
              y={H - 6}
              textAnchor="middle"
              fontSize={10}
              fontFamily="ui-sans-serif, sans-serif"
              fill="rgb(var(--text-muted))"
            >
              {seg.label}
            </text>
          </g>
        )
      })}

      {/* Y-axis tick labels */}
      {[0, 0.25, 0.5, 0.75, 1].map(t => {
        const val = dataMin + t * span
        const y = TOP + chartH - t * chartH
        return (
          <g key={t}>
            <line x1={LEFT - 4} x2={LEFT} y1={y} y2={y} stroke="rgb(var(--border))" strokeWidth={1} />
            <text
              x={LEFT - 8}
              y={y}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize={9}
              fontFamily="ui-monospace, monospace"
              fill="rgb(var(--text-muted))"
            >
              {fmtGBPCompact(val)}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

// ─── By-Owner Stacked Bar ─────────────────────────────────────────────────────

function OwnerStackedBar({ owners }: { owners: BucketItem[] }) {
  const total = owners.reduce((s, o) => s + Math.max(0, o.value_gbp), 0) || 1
  return (
    <div>
      <div className="flex h-6 rounded-lg overflow-hidden w-full">
        {owners.filter(o => o.value_gbp > 0).map((o, i) => (
          <div
            key={o.name}
            className="h-full transition-all duration-500"
            style={{
              width: `${(o.value_gbp / total) * 100}%`,
              backgroundColor: COLOURS[i % COLOURS.length],
            }}
            title={`${o.name}: ${fmtGBP(o.value_gbp)}`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
        {owners.filter(o => o.value_gbp > 0).map((o, i) => (
          <div key={o.name} className="flex items-center gap-1.5 text-xs">
            <span
              className="inline-block w-2.5 h-2.5 rounded-sm shrink-0"
              style={{ backgroundColor: COLOURS[i % COLOURS.length] }}
              aria-hidden
            />
            <span className="text-text-primary font-medium">{o.name}</span>
            <span className="text-text-muted">{fmtGBP(o.value_gbp)}</span>
            <span className="text-text-muted">({pct(o.value_gbp, total)})</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── By-Provider Bar ─────────────────────────────────────────────────────────

interface ProviderRow { name: string; value: number }

function ProviderBars({ providers, maxVal }: { providers: ProviderRow[]; maxVal: number }) {
  return (
    <div className="space-y-2.5">
      {providers.map((p, i) => (
        <div key={p.name} className="flex items-center gap-3">
          <span className="text-xs text-text-primary font-medium w-28 shrink-0 truncate">{p.name}</span>
          <div className="flex-1 h-2.5 bg-surface-hover rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${maxVal ? (Math.max(0, p.value) / maxVal) * 100 : 0}%`,
                backgroundColor: COLOURS[i % COLOURS.length],
              }}
            />
          </div>
          <span className="text-xs font-mono text-text-primary w-20 text-right shrink-0">
            {fmtGBPCompact(p.value)}
          </span>
        </div>
      ))}
    </div>
  )
}

// ─── FX Sensitivity ───────────────────────────────────────────────────────────
// "You hold £365k of USD assets; GBP +5% vs USD ⇒ −£18,250 to net worth."
// First-order, broad-GBP-move read. Hidden when there is no foreign exposure.

function FxSensitivityPanel() {
  const [fx, setFx] = useState<FxSensitivity | null>(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const d = await getFxSensitivity()
        if (!cancelled) setFx(d)
      } catch {
        if (!cancelled) setFx(null)
      } finally {
        if (!cancelled) setLoaded(true)
      }
    })()
    return () => { cancelled = true }
  }, [])

  if (!loaded) {
    return <div className="h-40 bg-surface-card rounded-xl animate-pulse" />
  }

  const foreign = fx?.total_foreign_exposure_gbp ?? 0
  // Nothing to show if the household has no foreign-currency exposure.
  if (!fx || foreign <= 0) return null

  const netWorth = fx.total_net_worth_gbp ?? 0
  const foreignPct = fx.foreign_pct ?? 0
  const scenarios = (fx.fx_scenarios ?? []).filter(s => s != null)
  // Currencies that actually carry FX sensitivity (exclude home GBP).
  const foreignRows = (fx.exposure_by_currency ?? [])
    .filter(r => r && r.currency !== 'GBP' && (r.value_gbp ?? 0) !== 0)
  const maxImpact = Math.max(1, ...scenarios.map(s => Math.abs(s.impact_gbp ?? 0)))

  return (
    <div className="card p-5 border border-amber-400/20">
      <h2 className="text-sm font-semibold text-text-primary mb-1 flex items-center gap-2">
        <Coins size={14} className="text-amber-400" aria-hidden />
        FX Sensitivity
        <span className="text-[10px] text-text-muted font-normal ml-1">(broad GBP move)</span>
      </h2>
      <p className="text-[11px] text-text-muted mb-4">
        <span className="text-text-secondary font-semibold">{fmtGBP(foreign)}</span> of your net
        worth ({foreignPct.toFixed(1)}%) is held in foreign currencies and moves with GBP.
      </p>

      {/* Per-currency foreign exposure */}
      {foreignRows.length > 0 && (
        <div className="space-y-2 mb-5">
          {foreignRows.map(r => (
            <div key={r.currency} className="flex items-center gap-3">
              <span className="text-xs font-mono font-bold text-text-primary w-10 shrink-0">{r.currency}</span>
              <div className="flex-1 h-2 bg-surface-hover rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-400 transition-all duration-500"
                  style={{ width: `${Math.min(100, Math.max(0, r.pct_of_net_worth ?? 0))}%` }}
                />
              </div>
              <span className="text-xs text-text-muted w-12 text-right shrink-0">{fmtPct(r.pct_of_net_worth, 1)}</span>
              <span className="text-xs font-mono text-text-primary w-20 text-right shrink-0">{fmtGBPCompact(r.value_gbp ?? 0)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Scenario bars — GBP −10%…+10% → ± impact to net worth */}
      <p className="text-[10px] uppercase tracking-wider text-text-muted mb-2">
        If GBP moves vs all foreign currencies
      </p>
      <div className="space-y-1.5">
        {scenarios.map(s => {
          const impact = s.impact_gbp ?? 0
          const neg = impact < 0
          const flat = impact === 0
          const width = (Math.abs(impact) / maxImpact) * 100
          const movePct = s.move_pct ?? 0
          const title = flat
            ? 'No move — no impact'
            : `GBP ${movePct > 0 ? '+' : ''}${movePct}% vs all foreign ⇒ ${neg ? '−' : '+'}${fmtGBP(Math.abs(impact))} `
              + `(${fmtPct(s.impact_pct, 2)} of net worth)`
              + (s.gbpusd_implied != null ? ` · GBP/USD ≈ ${s.gbpusd_implied.toFixed(3)}` : '')
          return (
            <div key={movePct} className="flex items-center gap-3" title={title}>
              <span className={clsx(
                'text-xs font-mono w-12 text-right shrink-0',
                movePct > 0 ? 'text-text-secondary' : movePct < 0 ? 'text-text-muted' : 'text-text-muted',
              )}>
                {movePct > 0 ? '+' : ''}{movePct}%
              </span>
              <div className="flex-1 h-3 bg-surface-hover rounded-full overflow-hidden relative">
                <div
                  className={clsx('h-full rounded-full transition-all duration-500',
                    flat ? 'bg-text-muted/30' : neg ? 'bg-rose-light' : 'bg-emerald-light')}
                  style={{ width: `${flat ? 0 : Math.max(2, width)}%` }}
                />
              </div>
              <span className={clsx(
                'text-xs font-mono w-24 text-right shrink-0 font-semibold',
                flat ? 'text-text-muted' : neg ? 'text-rose-light' : 'text-emerald-light',
              )}>
                {flat ? '—' : `${neg ? '−' : '+'}${fmtGBPCompact(Math.abs(impact))}`}
              </span>
            </div>
          )
        })}
      </div>

      {fx.note && (
        <p className="text-[10px] text-text-muted mt-4 leading-snug">{fx.note}</p>
      )}
    </div>
  )
}

// ─── FX Shock Panel ──────────────────────────────────────────────────────────

const FX_SHOCK_PRESETS: Array<{ label: string; value: number }> = [
  { label: '−10%', value: -10 },
  { label: '−5%', value: -5 },
  { label: '+5%', value: 5 },
  { label: '+10%', value: 10 },
]

/**
 * FxShockPanel — currency analogue of the price-shock scenario planner.
 *
 * Lets the user pick a GBP move (preset buttons or a slider) and immediately
 * sees the before/after net-worth impact broken down by currency.
 *
 * Convention (matches FX Sensitivity panel above):
 *   First-order linear: GBP +x% → foreign holdings worth x% LESS in GBP.
 *   GBP/GBX are unaffected.  Cash is valued RAW (consistent with /fx-sensitivity).
 */
function FxShockPanel() {
  const [movePct, setMovePct] = useState<number>(5)
  const [result, setResult] = useState<FxShockResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const runShock = async (pct: number) => {
    setLoading(true)
    setError(null)
    try {
      const r = await postFxShock({ gbp_move_pct: pct })
      setResult(r)
    } catch {
      setError('Could not compute FX shock — please try again.')
      setResult(null)
    } finally {
      setLoading(false)
    }
  }

  // Auto-run on first render with default move.
  useEffect(() => { runShock(movePct) }, [])   // eslint-disable-line react-hooks/exhaustive-deps

  const delta = result?.delta_gbp ?? 0
  const deltaPct = result?.delta_pct ?? 0
  const isNeg = delta < 0
  const isPos = delta > 0

  // Foreign rows only (GBP is unaffected, hide it to reduce noise).
  const foreignRows = (result?.by_currency ?? []).filter(r => r.currency !== 'GBP' && r.currency !== 'GBX')

  return (
    <div className="card p-5 border border-blue/20">
      <h2 className="text-sm font-semibold text-text-primary mb-1 flex items-center gap-2">
        <Coins size={14} className="text-blue-light" aria-hidden />
        FX Shock Scenario
        <span className="text-[10px] text-text-muted font-normal ml-1">(sustained GBP move)</span>
      </h2>
      <p className="text-[11px] text-text-muted mb-4">
        How much does a sustained GBP move change your net worth? GBP/GBX holdings are unaffected.
      </p>

      {/* Preset buttons */}
      <div className="flex flex-wrap gap-2 mb-3">
        {FX_SHOCK_PRESETS.map(p => (
          <button
            key={p.value}
            type="button"
            onClick={() => { setMovePct(p.value); runShock(p.value) }}
            className={clsx(
              'px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors',
              movePct === p.value
                ? p.value > 0
                  ? 'bg-rose/20 border-rose/50 text-rose-light'
                  : 'bg-emerald/20 border-emerald/50 text-emerald-light'
                : 'bg-surface-hover border-border text-text-secondary hover:border-blue/40',
            )}
            aria-pressed={movePct === p.value ? true : false}
          >
            GBP {p.label}
          </button>
        ))}
      </div>

      {/* Slider for custom value */}
      <div className="flex items-center gap-3 mb-4">
        <label className="text-[10px] uppercase tracking-wider text-text-muted shrink-0 w-16">
          Custom
        </label>
        <input
          type="range"
          min={-20}
          max={20}
          step={1}
          value={movePct}
          onChange={e => {
            const v = parseInt(e.target.value, 10)
            setMovePct(v)
            runShock(v)
          }}
          className="flex-1 accent-blue"
          aria-label="GBP move percentage"
        />
        <span className={clsx(
          'text-xs font-bold font-mono w-12 text-right shrink-0',
          movePct > 0 ? 'text-rose-light' : movePct < 0 ? 'text-emerald-light' : 'text-text-muted',
        )}>
          {movePct > 0 ? '+' : ''}{movePct}%
        </span>
      </div>

      {error && (
        <p className="text-xs text-rose-light mb-3">{error}</p>
      )}

      {loading && (
        <div className="h-28 bg-surface-hover rounded-lg animate-pulse mb-3" />
      )}

      {!loading && result && (
        <>
          {/* Headline impact */}
          <div className={clsx(
            'rounded-xl p-4 mb-4 border',
            isNeg ? 'bg-rose/5 border-rose/20' : isPos ? 'bg-emerald/5 border-emerald/20' : 'bg-surface-hover border-border',
          )}>
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-text-muted mb-0.5">Net worth impact</p>
                <p className={clsx(
                  'text-2xl font-extrabold font-mono',
                  isNeg ? 'text-rose-light' : isPos ? 'text-emerald-light' : 'text-text-muted',
                )}>
                  {isNeg ? '−' : isPos ? '+' : ''}£{Math.abs(delta).toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider text-text-muted mb-0.5">% of net worth</p>
                <p className={clsx(
                  'text-lg font-bold font-mono',
                  isNeg ? 'text-rose-light' : isPos ? 'text-emerald-light' : 'text-text-muted',
                )}>
                  {isNeg ? '−' : isPos ? '+' : ''}{Math.abs(deltaPct).toFixed(2)}%
                </p>
              </div>
            </div>
            <div className="flex gap-4 mt-3 text-[11px]">
              <div>
                <span className="text-text-muted">Before: </span>
                <span className="font-mono text-text-primary font-semibold">
                  £{(result.before_net_worth_gbp ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                </span>
              </div>
              <div>
                <span className="text-text-muted">After: </span>
                <span className={clsx('font-mono font-semibold', isNeg ? 'text-rose-light' : isPos ? 'text-emerald-light' : 'text-text-primary')}>
                  £{(result.after_net_worth_gbp ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                </span>
              </div>
              {result.gbpusd_rate > 0 && (
                <div>
                  <span className="text-text-muted">GBP/USD: </span>
                  <span className="font-mono text-text-secondary">{fmtNum(result.gbpusd_rate, 3)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Per-currency breakdown */}
          {foreignRows.length > 0 && (
            <div className="space-y-1.5 mb-4">
              <p className="text-[10px] uppercase tracking-wider text-text-muted mb-2">
                Impact by currency
              </p>
              {foreignRows.map(r => {
                const d = r.delta_gbp ?? 0
                const neg = d < 0
                const pos = d > 0
                return (
                  <div key={r.currency} className="flex items-center gap-3">
                    <span className="text-xs font-mono font-bold text-text-primary w-10 shrink-0">
                      {r.currency}
                    </span>
                    <span className="text-xs text-text-muted font-mono w-20 shrink-0">
                      £{(r.before_gbp ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                    </span>
                    <div className="flex-1 flex items-center">
                      <span className={clsx(
                        'text-xs font-mono font-semibold',
                        neg ? 'text-rose-light' : pos ? 'text-emerald-light' : 'text-text-muted',
                      )}>
                        {neg ? '−' : pos ? '+' : ''}£{Math.abs(d).toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                    <span className="text-[10px] text-text-muted w-20 text-right shrink-0">
                      after: £{(r.after_gbp ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                )
              })}
            </div>
          )}

          {/* Plain-English note */}
          {result.note && (
            <p className="text-[10px] text-text-muted leading-snug">{result.note}</p>
          )}
        </>
      )}
    </div>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function NetWorthPage() {
  const [data, setData] = useState<NetWorthSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<NetWorthSummary>('/api/net-worth/summary')
      setData(res.data)
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load net worth')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchData() }, [])

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (loading && !data) {
    return <PageLoader />
  }

  if (error || !data) {
    return <ErrorAlert message={error ?? 'No data available'} onRetry={fetchData} />
  }

  // ── Derived values ────────────────────────────────────────────────────────

  // Defensive: API may return partial data — default all arrays to []
  const byBucket   = data.by_bucket   ?? []
  const byOwner    = data.by_owner    ?? []
  const byWrapper  = data.by_wrapper  ?? []
  const byCurrency = data.by_currency ?? []
  const byAccount  = data.by_account  ?? []

  const tracked          = data.tracked_investments_gbp ?? 0
  const propertyGross    = data.property_gross_gbp ?? 0
  const liabilities      = data.liabilities_gbp ?? 0
  const propertyEquity   = data.property_net_equity_gbp ?? 0
  const discretionary    = data.discretionary_gbp ?? 0
  const nonDiscretionary = data.non_discretionary_gbp ?? 0
  const discretionaryPct = data.discretionary_pct ?? 0
  const netWorth         = data.total_net_worth_gbp ?? 0

  const cashSavings  = bucketVal(byBucket, 'cash', 'bank', 'savings')
  const fireApplicable = tracked // excludes property
  const totalAssets  = tracked + propertyGross + cashSavings
  const debtToAsset  = totalAssets > 0 ? Math.abs(liabilities) / totalAssets : 0
  const liquidVal    = tracked + cashSavings
  const liquidPct    = totalAssets > 0 ? (liquidVal / totalAssets) * 100 : 0
  const propEquityPct = propertyGross > 0
    ? (propertyEquity / propertyGross) * 100
    : 0
  const mortgageLTV = propertyGross > 0
    ? (Math.abs(liabilities) / propertyGross) * 100
    : 0

  // Waterfall bars
  const waterfallBars: WaterfallBar[] = [
    {
      label: 'Investments',
      value: tracked,
      colour: '#3b82f6',
    },
    {
      label: 'Property',
      value: propertyGross,
      colour: '#10b981',
    },
    {
      label: 'Liabilities',
      value: liabilities,
      colour: '#ef4444',
    },
    {
      label: 'Net Worth',
      value: netWorth,
      isTotal: true,
      colour: '#8b5cf6',
    },
  ]

  // By provider — group accounts by provider short-name
  const providerMap: Record<string, number> = {}
  for (const acc of byAccount) {
    const p = providerFrom(acc.name)
    providerMap[p] = (providerMap[p] ?? 0) + acc.value_gbp
  }
  const providers: ProviderRow[] = Object.entries(providerMap)
    .filter(([, v]) => v > 0)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
  const providerMax = providers[0]?.value ?? 1

  // Breakdown cards definition
  const cards = [
    {
      label: 'Tracked Investments',
      value: tracked,
      sub: `${pct(tracked, netWorth)} of net worth`,
      icon: TrendingUp,
      colour: 'text-blue-light',
      accent: 'border-blue/20',
    },
    {
      label: 'Property (gross)',
      value: propertyGross,
      sub: `Net equity ${fmtGBP(propertyEquity)}`,
      icon: Home,
      colour: 'text-emerald-light',
      accent: 'border-emerald/20',
    },
    {
      label: 'Cash & Savings',
      value: cashSavings,
      sub: `${pct(cashSavings, netWorth)} of net worth`,
      icon: Banknote,
      colour: 'text-amber-400',
      accent: 'border-amber-400/20',
    },
    {
      label: 'Liabilities',
      value: liabilities,
      sub: 'Mortgages & debt',
      icon: Coins,
      colour: 'text-rose-light',
      accent: 'border-rose/20',
    },
    {
      label: 'Net Worth',
      value: netWorth,
      sub: 'Investments + property − liabilities',
      icon: Wallet,
      colour: 'text-purple-light',
      accent: 'border-purple/20',
      large: true,
    },
    {
      label: 'FIRE-Applicable',
      value: fireApplicable,
      sub: 'Excludes property',
      icon: TrendingUp,
      colour: 'text-cyan-400',
      accent: 'border-cyan-400/20',
    },
  ]

  return (
    <div className="space-y-6 animate-fade-in">

      <PageHeader
        title="Net Worth"
        icon={Wallet}
        subtitle="Full household picture — tracked investments + property − liabilities"
        actions={
          <button
            type="button"
            onClick={fetchData}
            className="btn-secondary gap-2 text-xs"
            disabled={loading}
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
            Refresh
          </button>
        }
      />

      {/* ── Hero number with 30-day trend ───────────────────────────────────── */}
      <NetWorthHero
        netWorth={netWorth}
        repriceAt={data.most_recent_reprice_at}
        staleSeverity={data.prices_stale_severity}
      />


      {/* ── 2×3 Breakdown cards ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 stagger-children">
        {cards.map(card => {
          const Icon = card.icon
          return (
            <div
              key={card.label}
              className={clsx(
                'card p-4 border',
                card.accent,
                card.large && 'ring-1 ring-purple/30',
              )}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <p className="panel-title leading-tight">
                  {card.label}
                </p>
                <Icon size={13} className={clsx('shrink-0 mt-0.5', card.colour)} aria-hidden />
              </div>
              <p className={clsx(
                'font-bold tabular-nums',
                card.large ? 'text-2xl' : 'text-xl',
                card.value < 0 ? 'text-rose-light' : card.colour,
              )}>
                {fmtGBP(card.value)}
              </p>
              <p className="text-[10px] text-text-muted mt-1 leading-snug">{card.sub}</p>
            </div>
          )
        })}
      </div>

      {/* ── Key metrics row ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          {
            label: 'Debt-to-Asset',
            value: `${(debtToAsset * 100).toFixed(1)}%`,
            note: debtToAsset < 0.3 ? 'Low leverage' : debtToAsset < 0.6 ? 'Moderate' : 'High leverage',
            ok: debtToAsset < 0.5,
          },
          {
            label: 'Liquid vs Illiquid',
            value: `${liquidPct.toFixed(0)}% liquid`,
            note: `${fmtGBP(liquidVal)} accessible`,
            ok: liquidPct > 40,
          },
          {
            label: 'Property Equity',
            value: `${propEquityPct.toFixed(1)}%`,
            note: `${fmtGBP(data.property_net_equity_gbp)} net equity`,
            ok: propEquityPct > 50,
          },
          {
            label: 'Mortgage LTV',
            value: `${mortgageLTV.toFixed(1)}%`,
            note: mortgageLTV < 60 ? 'Strong position' : mortgageLTV < 80 ? 'Standard' : 'High LTV',
            ok: mortgageLTV < 75,
          },
        ].map(m => (
          <div key={m.label} className="stat-card">
            <p className="stat-label">{m.label}</p>
            <p className={clsx('stat-value', m.ok ? 'text-emerald-light' : 'text-amber-400')}>
              {m.value}
            </p>
            <p className="text-[10px] text-text-muted">{m.note}</p>
          </div>
        ))}
      </div>

      {/* ── Waterfall chart ──────────────────────────────────────────────────── */}
      <div className="card p-5">
        <h2 className="panel-title mb-1">
          Net Worth Build-Up
        </h2>
        <p className="text-[11px] text-text-muted mb-4">
          How tracked investments + property − liabilities produces your net worth
        </p>
        <WaterfallChart bars={waterfallBars} />
        <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3">
          {[
            { label: 'Investments', colour: '#3b82f6' },
            { label: 'Property',    colour: '#10b981' },
            { label: 'Liabilities', colour: '#ef4444' },
            { label: 'Net Worth',   colour: '#8b5cf6' },
          ].map(l => (
            <div key={l.label} className="flex items-center gap-1.5 text-[11px] text-text-muted">
              <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ backgroundColor: l.colour }} aria-hidden />
              {l.label}
            </div>
          ))}
        </div>
      </div>

      {/* ── By Owner ────────────────────────────────────────────────────────── */}
      <div className="card p-5">
        <h2 className="panel-title mb-4">By Owner</h2>
        {byOwner.length === 0 ? (
          <p className="text-sm text-text-muted text-center py-4">No owner data available</p>
        ) : (
        <>
        <OwnerStackedBar owners={byOwner} />
        <div className="grid sm:grid-cols-3 gap-3 mt-5">
          {byOwner.map((o, i) => (
            <div key={o.name} className="stat-card border" style={{ borderColor: COLOURS[i % COLOURS.length] + '33' }}>
              <p className="stat-label">{o.name}</p>
              <p className="stat-value" style={{ color: COLOURS[i % COLOURS.length] }}>
                {fmtGBPCompact(o.value_gbp)}
              </p>
              <p className="text-[10px] text-text-muted">{fmtPct(o.pct_of_net_worth, 1)} of net worth</p>
            </div>
          ))}
        </div>
        </>
        )}
      </div>

      {/* ── By Provider ─────────────────────────────────────────────────────── */}
      <div className="card p-5">
        <h2 className="panel-title mb-4">By Provider</h2>
        {providers.length === 0 ? (
          <p className="text-sm text-text-muted text-center py-4">No account data available</p>
        ) : (
          <ProviderBars providers={providers} maxVal={providerMax} />
        )}
      </div>

      {/* ── Discretionary control split ─────────────────────────────────────── */}
      <div className="card p-5">
        <h2 className="panel-title mb-3">Control Split</h2>
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 h-3 bg-surface-hover rounded-full overflow-hidden flex">
            <div
              className="h-full bg-emerald transition-all duration-500"
              style={{ width: `${discretionaryPct}%` }}
            />
          </div>
          <span className="text-xs text-text-muted shrink-0 w-24 text-right">
            {discretionaryPct.toFixed(1)}% touchable
          </span>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <p className="text-[11px] text-text-muted uppercase tracking-wider mb-1">Discretionary</p>
            <p className="text-2xl font-bold text-emerald-light tabular-nums">
              {fmtGBP(discretionary)}
            </p>
            <p className="text-[10px] text-text-muted mt-0.5">You can trade / rebalance</p>
          </div>
          <div>
            <p className="text-[11px] text-text-muted uppercase tracking-wider mb-1">Manager-controlled / Illiquid</p>
            <p className="text-2xl font-bold text-text-secondary tabular-nums">
              {fmtGBP(nonDiscretionary)}
            </p>
            <p className="text-[10px] text-text-muted mt-0.5">DB pension, manager funds, property</p>
          </div>
        </div>
      </div>

      {/* ── By Tax Wrapper ───────────────────────────────────────────────────── */}
      <div className="card p-5">
        <h2 className="panel-title mb-3">By Tax Wrapper</h2>
        {byWrapper.length === 0 ? (
          <p className="text-sm text-text-muted text-center py-4">No wrapper data available</p>
        ) : (
        <div className="space-y-2">
          {byWrapper.map((w, i) => (
            <div
              key={w.name}
              className="flex items-center gap-3 py-2 border-b border-border last:border-b-0"
            >
              <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: COLOURS[i % COLOURS.length] }} aria-hidden />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-text-primary">{w.name}</span>
                  <span className="text-[10px] text-text-muted">{fmtPct(w.pct_of_net_worth, 1)}</span>
                </div>
                <div className="mt-1 h-1.5 bg-surface-hover rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.max(0, w.pct_of_net_worth ?? 0)}%`,
                      backgroundColor: COLOURS[i % COLOURS.length],
                    }}
                  />
                </div>
              </div>
              <p className={clsx(
                'text-sm font-bold font-mono shrink-0 w-24 text-right',
                w.value_gbp < 0 ? 'text-rose-light' : 'text-text-primary',
              )}>
                {fmtGBPCompact(w.value_gbp)}
              </p>
            </div>
          ))}
        </div>
        )}
      </div>

      {/* ── Currency exposure ────────────────────────────────────────────────── */}
      <div className="card p-5">
        <h2 className="panel-title mb-3 flex items-center gap-2">
          <Coins size={14} className="text-blue-light" aria-hidden />
          Currency Exposure
          <span className="text-[10px] text-text-muted font-normal normal-case tracking-normal ml-1">(tracked investments)</span>
        </h2>
        {byCurrency.length === 0 ? (
          <p className="text-sm text-text-muted text-center py-4">No currency data available</p>
        ) : (
        <div className="space-y-2">
          {byCurrency.map(c => (
            <div key={c.currency} className="flex items-center gap-3">
              <span className="text-xs font-mono font-bold text-text-primary w-10 shrink-0">{c.currency}</span>
              <div className="flex-1 h-2 bg-surface-hover rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue transition-all duration-500"
                  style={{ width: `${Math.max(0, c.pct_of_tracked ?? 0)}%` }}
                />
              </div>
              <span className="text-xs text-text-muted w-10 text-right shrink-0">{fmtPct(c.pct_of_tracked, 1)}</span>
              <span className="text-xs font-mono text-text-primary w-20 text-right shrink-0">{fmtGBPCompact(c.value_gbp)}</span>
            </div>
          ))}
        </div>
        )}
      </div>

      {/* ── FX Sensitivity (hidden when no foreign exposure) ─────────────────── */}
      <FxSensitivityPanel />

      {/* ── FX Shock Scenario (sustained GBP move impact) ────────────────────── */}
      <FxShockPanel />

      {/* ── All Accounts ─────────────────────────────────────────────────────── */}
      <div className="card p-5">
        <h2 className="panel-title mb-3">
          All Accounts
          <span className="text-[10px] text-text-muted font-normal normal-case tracking-normal ml-2">
            {byAccount.length} accounts
          </span>
        </h2>
        {byAccount.length === 0 ? (
          <p className="text-sm text-text-muted text-center py-4">No accounts linked yet</p>
        ) : (
        <div className="space-y-1">
          {byAccount.map(acc => (
            <div
              key={acc.id}
              className="flex items-center gap-3 py-2 border-b border-border last:border-b-0"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-text-primary truncate">{acc.name}</p>
                  {acc.discretionary
                    ? (
                      <span className="badge-green text-[9px] px-1.5 py-0.5">You control</span>
                    )
                    : (
                      <span className="badge-neutral text-[9px] px-1.5 py-0.5">View only</span>
                    )}
                </div>
                <p className="text-[10px] text-text-muted">
                  {acc.type} · {acc.owner} · {acc.holdings_count} holding{acc.holdings_count !== 1 ? 's' : ''}
                </p>
              </div>
              <p className={clsx(
                'text-sm font-bold font-mono shrink-0',
                acc.value_gbp < 0 ? 'text-rose-light' : 'text-text-primary',
              )}>
                {fmtGBPCompact(acc.value_gbp)}
              </p>
            </div>
          ))}
        </div>
        )}
      </div>

    </div>
  )
}
